import { useAskAiStore } from '@/store/askAiStore';
import { cn } from '@/lib/utils';
import { getDetectiveChips } from './detectiveChipsConfig';

export interface DetectiveChipsProps {
  /** Module key from `detectiveChipsConfig` — unknown keys render nothing. */
  module: string;
  className?: string;
}

/**
 * `<DetectiveChips>` — the per-page "Ask the Detective" prompt row.
 *
 * A slim strip of pre-written questions that open the Ask AI panel AND submit
 * in one click (`askAiStore.ask`), so every module can demonstrate the AI
 * without the user having to invent a question that the retriever can ground.
 *
 * The questions are curated in `detectiveChipsConfig.ts` — they are all
 * rule/term EXPLANATION questions, because those are the ones the RAG corpus
 * (rule definitions + glossary) can answer. Live-count questions ("how many
 * alerts right now") get refused, so they are deliberately absent.
 *
 * Renders `null` when the module has no configured questions, which makes it
 * safe to drop onto a page before its questions have been written.
 */
export function DetectiveChips({
  module,
  className,
}: DetectiveChipsProps): JSX.Element | null {
  const ask = useAskAiStore((s) => s.ask);
  const questions = getDetectiveChips(module);

  if (questions.length === 0) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px]',
        className,
      )}
    >
      <span className="inline-flex shrink-0 items-center gap-1.5 font-semibold text-gray-500 dark:text-muted-foreground">
        <span aria-hidden className="text-[15px] leading-none">
          🕵️
        </span>
        Ask the Detective:
      </span>

      {questions.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => ask(q)}
          aria-label={`Ask the Detective: ${q}`}
          title={q}
          className={cn(
            // Navy outline at rest…
            'max-w-full rounded-full border border-brand/30 bg-white px-2.5 py-1',
            'text-left text-[12px] font-medium text-brand',
            'dark:border-white/20 dark:bg-transparent dark:text-foreground',
            // …soft-green on hover/focus.
            'transition-colors hover:border-emerald-400/70 hover:bg-emerald-50 hover:text-brand-deep',
            'dark:hover:border-emerald-300/60 dark:hover:bg-emerald-400/10',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70',
          )}
        >
          {/* Long questions wrap to at most two lines on narrow screens
              instead of forcing the row to scroll. */}
          <span className="line-clamp-2">{q}</span>
        </button>
      ))}
    </div>
  );
}

export default DetectiveChips;
